import { Injectable } from '@angular/core';
import { ReqService } from './req.service';
import { URLService } from './url.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * This Search State Service is an abstraction that gathers all the pieces of
 * data that compose the conditions of an specific search context.
 *
 * You can always infer a state just by looking at the url. This service
 * facilitates the job of changing states without making url string
 * manipulations.
 *
 * When getting info from a state, the info provided is a string with the same
 * format as the URL. For some pieces of data, there are some methods to get
 * the same info in a more convenient format.
 *
 * When setting a part of the current state, it must be done as the same
 * format as it would display in the URL.
 */
@Injectable()
export class SearchStateService {
  public getFiltersFromUrl: any;
  public currentQuery;
  public pathArray;
  public slug: any;
  public section: any;
  public date: any;
  public currencyCode: any;
  public rooms: any;
  public type: any;
  public offset: any;
  public filters: any;
  public limit: any;
  public sortBy: any;
  public sortCriteria: any;
  public priceBy: any;
  public paymentMethod: any;
  public featuredHotelId: any;
  public flexible: any;
  public popups: any;
  public possibleFilters: string[] = [
    'NAME',
    'PRICE_RANGE',
    'STAR',
    'OFFERS',
    'AMENITY',
    'NEARBY',
    'MEAL',
    'PAYMENT_METHOD',
    'DISCOUNT_RANGE',
    'CYBER_MONDAY',
    'ACCOMMODATION_TYPE',
    'OUTSTANDING',
    'RATING',
  ];
  private _stateUpdated$: Subject<boolean> = new BehaviorSubject(false);

  constructor(
    public reqService: ReqService,
    private urlService: URLService,
  ) {
    this.setState();
  }

  private getFiltering(filterObject) {
    const query = this.possibleFilters.reduce((previous, current) => {
      if (filterObject[current] !== undefined) {
        return (previous += current + '=' + filterObject[current] + '&');
      } else {
        return (previous += '');
      }
    }, '');
    return query.substring(0, query.length - 1);
  }

  /**
   * Sets the state according to the current URL
   */
  public getLocationSearch() {
    const query = this.getParamsCurrentURL();
    for (const param in query) {
      if (query[param] && typeof query[param] !== 'string') {
        query[param] = query[param][0];
      }
    }

    if (!query.withFullPrice) {
      query.withFullPrice = 'true';
    }

    return query;
  }

  /**
   * Verify if exists a params for AB testing (only for QA)
   * @returns value | undefined
   */
  public isABRunning(param: string) {
    const urlParams = this.getParamsCurrentURL();
    return urlParams[param];
  }

  /**
   * Get params from URL , return object with key value
   * example ?q=33&id=55 => { q:33, id:55 }
   * @return {Object}
   */
  public getParamsCurrentURL() {
    const url: string = location.search;
    const search: any = new URLSearchParams(url);
    const params: any = {};
    search.forEach((value, query) => (params[query] = value));
    if (!params.withFullPrice) {
      params.withFullPrice = 'true';
    }
    return params;
  }

  private normaliceSlug(slug) {
    slug = slug.split('-');
    const entityId = slug.pop();
    slug = [slug.join('-')];
    slug[0] = this.urlService.normalizeUrl(slug[0]);
    slug.push(entityId);
    slug = slug.join('-');
    return slug;
  }

  public setState() {
    this.currentQuery = this.getLocationSearch();
    this.getQueryString()
      .slice(1)
      .split('&')
      .map((item) => item.split('='))
      .forEach((item) => {
        this.currentQuery[item[0]] = item[1];
      });
    this.pathArray = location.pathname.split('/');
    this.slug = this.normaliceSlug(this.pathArray.pop());
    this.section = this.pathArray.pop();
    this.date = this.currentQuery.date;
    this.currencyCode = this.currentQuery.currencyCode || 'ARS';
    this.rooms = this.currentQuery.rooms;
    this.featuredHotelId = this.currentQuery.featuredHotelId;
    this.type = this.currentQuery.type || 'CITY';
    this.offset = this.currentQuery.offset;
    this.filters = this.getFiltering(this.currentQuery);
    this.limit = this.currentQuery.limit;
    this.sortBy = this.currentQuery.sortBy;
    this.sortCriteria = this.currentQuery.sortCriteria;
    this.priceBy = this.currentQuery.priceBy || 'NIGHT';
    this.flexible = this.currentQuery.flexible
      ? this.currentQuery.flexible.toLocaleLowerCase() === 'true'
      : false;
    this.popups = this.currentQuery.popups;
    this.paymentMethod = this.currentQuery.PAYMENT_METHOD;
    this._stateUpdated$.next(true);
  }

  // Mandatory parameters for all hotel navigation
  public getSection() {
    return this.section;
  }

  public setSection(value) {
    this.section = value;
  }

  public getSlug() {
    return this.slug;
  }

  public setSlug(value) {
    this.slug = this.normaliceSlug(value);
  }

  public getDate() {
    return this.date;
  }

  public getCurrencyCode() {
    return this.currencyCode;
  }

  public setDate(value) {
    this.date = value;
  }

  public getRooms() {
    return this.rooms;
  }

  public setRooms(value) {
    this.rooms = value;
  }

  // Parameters for the result section of the hotel app
  public getType() {
    return this.type;
  }

  public setType(value) {
    this.type = value;
  }

  public getOffset() {
    return this.offset;
  }

  public setOffset(value) {
    this.offset = value;
  }

  public getLimit() {
    return this.limit;
  }

  public setLimit(value) {
    this.limit = value;
  }

  public getFilters() {
    return this.filters;
  }

  public setFilters(value) {
    this.filters = value;
  }

  public getSortBy() {
    return this.sortBy;
  }

  public setSortBy(value) {
    this.sortBy = value;
  }

  public getSortCriteria() {
    return this.sortCriteria;
  }

  public setSortCriteria(value) {
    this.sortCriteria = value;
  }

  public getPriceBy() {
    return this.priceBy;
  }

  public setPriceBy(value) {
    this.priceBy = value;
  }

  public getPaymentMethod() {
    if (!this.paymentMethod) {
      return 'all';
    } else {
      const paymentMethods = this.paymentMethod.split(',');
      return paymentMethods.length === 1 ? paymentMethods[0] : 'all';
    }
  }

  public setPaymentMethod(value) {
    this.paymentMethod = value;
  }

  public setCurrencyCode(value) {
    this.currencyCode = value;
  }

  public getEntityCode() {
    return this.getSlug().split('-').pop();
  }

  public getEntityName() {
    let slugArray = this.getSlug().split('-');
    slugArray.pop();
    slugArray = slugArray.map((word) => {
      return word.substr(0, 1).toUpperCase() + word.substr(1);
    });
    return decodeURI(slugArray.join(' '));
  }

  public getPage() {
    const page = (this.getOffset() ? this.getOffset() : 0) / this.getLimit() + 1;
    return page;
  }

  /**
   * Takes a hotel name and turns it into slug format.
   * (ie: Hotel Name to hotel-name)
   * @param {String} string
   * @return {string}
   */
  public slugify(string) {
    return string
      .toString()
      .toLowerCase()
      .replace(/(<([^>]+)>)/gi, '')
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/\-\-+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '');
  }

  /**
   * Gets the date information of a given state as an object with 'from' and 'to' attributes
   * @return {Object}
   */
  public getDecodedDate() {
    if (!this.date) {
      return false;
    }
    const decoded: any = {};
    const dateArray = this.date.split(',');
    decoded.from = dateArray[0];
    decoded.to = dateArray[1];
    return decoded;
  }

  /**
   * Gets the room information of a given state as a distribution object.
   * @return {Object}
   */
  public getDecodedRooms() {
    if (!this.rooms) {
      return {
        rooms: [
          {
            adults: 2,
            children: [],
          },
        ],
        total: 2,
        totalAdults: 2,
        totalChildren: 0,
      };
    }
    const distribution = {
      rooms: [],
      total: 0,
      totalAdults: 0,
      totalChildren: 0,
    };
    const roomsArray = this.rooms.split('!');
    roomsArray.forEach((room) => {
      const dist: any = {};
      room = room.split('-');
      dist.adults = parseInt(room.shift(), 10);
      dist.children = room.map((children) => parseInt(children, 10));
      distribution.totalAdults = distribution.totalAdults + dist.adults;
      distribution.totalChildren = distribution.totalChildren + dist.children.length;
      distribution.total = distribution.total + dist.adults + dist.children.length;
      distribution.rooms.push(dist);
    });

    return distribution;
  }

  /**
   * Gets the filters information of a given state as an object.
   * @return {Object}
   */
  public getDecodedFilters() {
    if (!this.filters) {
      return {};
    }

    const decodedFilters = {};

    this.filters.split('&').forEach((filter) => {
      filter = filter.split('=');
      decodedFilters[filter[0]] =
        filter[1].indexOf(',') !== -1 ? filter[1].split(',') : [filter[1]];
    });

    return decodedFilters;
  }

  /**
   * Gets the id from the entity slug.
   * @return {String}
   */
  public getSlugId() {
    return this.slug.split('-').pop();
  }

  /**
   * Gets the query part of the state
   * @returns {string}
   */
  public getQuery(trackEvent?) {
    const search: any = new URLSearchParams(this.getQueryString());
    const params: any = {};
    search.forEach((value, query) => (params[query] = value));
    if (window.dynamicEnabled) {
      params['dynamicEnabled'] = true;
    }
    if (window.withFullPrice) {
      params['withFullPrice'] = true;
    }
    if (trackEvent) {
      params['amTrackEvent'] = true;
    }
    return params;
  }

  /**
   * Gets the query string part of the state
   * @returns {string}
   */
  public getQueryString() {
    const stateQuery = {
      currencyCode: this.currencyCode,
      date: this.date,
      rooms: this.rooms,
      featuredHotelId: this.featuredHotelId,
      type: this.type,
      offset: this.offset,
      filters: this.filters,
      limit: this.limit,
      sortBy: this.sortBy,
      sortCriteria: this.sortCriteria,
      priceBy: this.priceBy,
      flexible: this.flexible,
    };

    let queryString = '';
    for (const key in stateQuery) {
      if (stateQuery[key] !== undefined && stateQuery[key] !== '') {
        queryString +=
          (queryString.indexOf('?') === -1 ? '?' : '&') +
          (key !== 'filters' ? key + '=' : '') +
          stateQuery[key];
      }
    }

    for (const key in this.getLocationSearch()) {
      if (
        !(key in stateQuery) &&
        !this.possibleFilters.some((filter) => key === filter)
      ) {
        queryString +=
          (queryString.indexOf('?') === -1 ? '?' : '&') +
          key +
          '=' +
          this.getLocationSearch()[key];
      }
    }
    return queryString;
  }

  /**
   * Sets or unsets an specific filter value
   * @param {String} type
   * @param {*} code
   */
  public toggleFilterItem(type, code) {
    const current = this.getDecodedFilters();
    if (!current.hasOwnProperty(type)) {
      current[type] = [code];
    } else {
      if (type === 'NAME' || type === 'PRICE_RANGE') {
        current[type] = code === '' ? [] : [code];
      } else {
        const options = current[type];
        const removed = options.find((value, index, array) => {
          if (value === code) {
            array.splice(index, 1);
            return true;
          }
        });
        if (removed === undefined) {
          options.push(code);
        }
      }
    }
    if (current[type].length === 0) {
      this.deleteFilter(type, current);
    }
    this.setFilters(this.getFiltering(current));
  }

  /**
   * Completely removes a filter type
   * @param {String} type
   * @param {Object} stateObject OPTIONAL Specific stateObject where the
   * filter will be deleted. If not provided, currentState is used.
   */
  public deleteFilter(type, stateObject?) {
    const current = stateObject ? stateObject : this.getDecodedFilters();
    delete current[type];
    this.setFilters(this.getFiltering(current));
  }

  /**
   * Encodes a decoded distribution object into a string
   * @param {Object} distributionObject
   * @return {string}
   */
  public encodeDistribution(distributionObject) {
    return distributionObject.rooms
      .map((room) => {
        let children = '';
        if (room.children.length) {
          children = '-' + room.children.join().replace(/,/g, '-');
        }
        return room.adults + children;
      })
      .join('!');
  }

  /**
   * Iterates over the current state object and redirects the location
   * according to the information previously set in the current state
   */
  public redirect(anotherTab?) {
    window.open(this.getRedirectUrl(), anotherTab ? '_blank' : '_self');
  }

  /**
   * Iterates over the current state object and return the url to redirect
   * according to the information previously set in the current state
   */
  public getRedirectUrl(slug?) {
    return `/hotels/${this.section}/${slug || this.slug}${this.getQueryString()}`;
  }

  /**
   * Hash to not use GIATA ids
   * @returns {string}
   */
  public getHashedId(slugId) {
    slugId = slugId.toString();
    const slugNumber = parseInt(slugId[0], 10);
    const n1 = (slugNumber * 5).toString()[0];
    const n2 = (slugNumber * 7).toString()[0];
    const n3 = (slugNumber * 9).toString()[0];
    return slugId + n1 + n2 + n3 + 'H';
  }

  public getFeaturedHotelId() {
    return this.featuredHotelId;
  }

  public setFeaturedHotelId(value) {
    this.featuredHotelId = value;
  }

  public getFlexible() {
    return this.flexible;
  }

  public setFlexible(value) {
    this.flexible = value;
  }

  public getPopups() {
    return this.popups;
  }

  public setPopups(value) {
    this.popups = value;
  }

  get stateUpdated$(): Observable<boolean> {
    return this._stateUpdated$.asObservable();
  }
}
